/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/* HEADER STICKY */
const headerStickyElement = document.querySelector('.cc-header');
const headerStickyObserver = new IntersectionObserver( 
  ([e]) => e.target.classList.toggle('is-sticky', e.intersectionRatio < 1),
  {threshold: [1]}
);
headerStickyObserver.observe(headerStickyElement);

/* COLLECTION SEO */
const collSeoMenuMain = document.querySelector('.section-collection-seo a.open-collection-seo');
const collSeoContentMain = document.querySelector('.section-collection-seo .mode-product');
const collSeoMenu = document.querySelectorAll('.section-collection-seo .menu a.menu-item');
const collSeoContent = document.querySelectorAll('.section-collection-seo .content-item');
const collSeoAccordion = document.querySelectorAll('.section-collection-seo .content-item .accordion');

if (collSeoMenuMain && collSeoContentMain) {
  collSeoMenuMain.addEventListener('click', function(e){
    e.preventDefault();
    
    if (collSeoContentMain.classList.contains('open')) collSeoContentMain.classList.remove('open');
    else collSeoContentMain.classList.add('open');
  }, false);
}

if (collSeoMenu) {
  collSeoMenu.forEach((btn) => {
    btn.addEventListener('click', function(e){
      e.preventDefault();

      if (collSeoMenuMain && collSeoContentMain && !collSeoContentMain.classList.contains('open')) {
        collSeoMenuMain.click();
      }
      
      let index = this.dataset.index;
      collSeoMenu.forEach((btn) => {
        if (btn.dataset.index == index) btn.classList.add('active');
        else btn.classList.remove('active');
      });
      collSeoContent.forEach((div) => {
        if (div.dataset.index == index) div.style.display = 'block';
        else div.style.display = 'none';
      });
      collSeoAccordion.forEach((div) => {
        div.classList.remove('active');
      });
    }, false);
  });
}

const collSeoAccordionOpen = document.querySelectorAll('.section-collection-seo .content-item .open-accordion, .section-collection-seo .content-item .open-accordion-n2, .section-collection-seo .content-item .open-accordion-n3');
if (collSeoAccordionOpen) {
  collSeoAccordionOpen.forEach((btn) => {
    btn.addEventListener('click', function(e){
      e.preventDefault();
      this.parentNode.classList.toggle('active');
    }, false);
  });
}

/* SWITCHER LANG */
const switcherElement = document.querySelector('.language-switcher');
const switcherElementBtn = document.querySelector('.language-switcher .btn-open');
const switcherElementLinks = document.querySelectorAll('.language-switcher .list a');
if (switcherElement && switcherElementBtn) {
  switcherElementBtn.addEventListener('click', function(e){
    e.preventDefault();
    
    if (switcherElement.classList.contains('open')) {
      switcherElement.classList.remove('open');
      switcherElement.removeEventListener('mouseleave');
    } else {
      switcherElement.classList.add('open');
      switcherElement.addEventListener('mouseleave', event => {
        switcherElement.classList.remove('open');
      });
    }
  }, false);

  switcherElementLinks.forEach((btn) => {
    btn.addEventListener('click', function(e){
      e.preventDefault();
      window.location.replace(this.getAttribute("href"));
    }, false);
  });

  /*document.addEventListener('click', event => {
    const switcherElementClickInside = switcherElement.contains(event.target);
    if (!switcherElementClickInside && switcherElement.classList.contains('open')) switcherElement.classList.remove('open');
  });*/

  /* NotifyMe app customization */
  addEventListener("DOMContentLoaded", (event) => {
    const produtForm = document.querySelector('.product-info__form .js-product-form-main');
    if (produtForm) {
      const config = { attributes: false, childList: true, subtree: true };
  
      const callback = (mutationList) => {
        for (const mutation of mutationList) {
          if (mutation.type === "childList") {
            if (mutation.target.classList.contains('product-info__add-button')) {
              const notifyMeDescription = document.querySelector('.product-info__add-button .nm-portal');
              (notifyMeDescription) && produtForm.prepend(notifyMeDescription);
            }
          }
        }
      };
      const observer = new MutationObserver(callback);
      observer.observe(produtForm, config);
    }
  });
  /*End NotifyMe app customization */
}

/* CUSTOM CROSS SELL */
document.addEventListener('on:cart-drawer:before-open', (event) => {
  console.log('before');
  //handleCustomCrossSellUpdate();
  setTimeout(handleCustomCrossSellUpdate, 1000);
});
document.addEventListener('on:line-item:change', (event) => {
  console.log('change');
  handleCustomCrossSellUpdate();
});
document.addEventListener('dispatch:cart-drawer:refresh', (event) => {
  console.log('refresh');
  setTimeout(handleCustomCrossSellUpdate, 2000);
});
handleCustomCrossSellUpdate();

async function handleCustomCrossSellUpdate() {
  try {
    const crossSellContainer = document.querySelector('#cart-custom-crosssell');
    if (!crossSellContainer) return;
    
    crossSellContainer.innerHTML = '';

    const cart = await fetch('/cart.js').then(r => r.json());
    if (!cart || !cart.items.length) return;    

    for (const item of cart.items) {
      const query = `
        query($handle: String!, $language: LanguageCode) @inContext(language: $language) {
          product(handle: $handle) {
            crossSellPercent: metafield(namespace: "custom", key: "custom_cross_sell_prct") {
              value
            }
            crossSellProduct: metafield(namespace: "custom", key: "custom_cross_sell_2") {
              value
              reference {
                ... on Product {
                  handle
                  title
                  featuredImage { url }
                  variants(first: 1) {
                    edges {
                      node {
                        id
                        price { amount currencyCode }
                        compareAtPrice { amount currencyCode }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const res = await fetch('/api/2024-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': 'cfe402f96e25e1a7e20d59c42e10d734',
        },
        body: JSON.stringify({ query, variables: { handle: item.handle, language: String(Shopify.locale).toUpperCase()} }),
      });

      const data = await res.json();
      const metafieldCrossSell = data.data.product.crossSellProduct;
      const metafieldDiscount = data.data.product.crossSellPercent;
      const cross = metafieldCrossSell?.reference;
      const discount = metafieldDiscount?.value;

      if (!cross) continue;

      let alreadyInCart = false;
      for (const item2 of cart.items) {
        if (item2.handle == cross.handle) {
          alreadyInCart = true;
          break;
        }
      }
      if (alreadyInCart) continue;

      showCrossSell(crossSellContainer, cross, discount);
    }
  } catch (err) {
    console.error('Erreur CrossSell :', err);
  }
}

function showCrossSell(container, cross, discount) {
  const variant = cross.variants.edges[0]?.node;
  if (!variant || !discount) return;

  const variantId = variant.id.split('/').pop();
  const price = parseFloat(variant.price.amount).toFixed(2);
  const prct_discount = parseFloat(discount).toFixed(2);
  const compareAt = variant.compareAtPrice?.amount ? parseFloat(variant.compareAtPrice.amount).toFixed(2) : null;
  const isDiscounted = compareAt && compareAt > price;
  const discountPercent = isDiscounted ? Math.round(100 - (price / compareAt) * 100) : null;
  const currency = variant.price.currencyCode || '';
  const language = String(Shopify.locale).toLowerCase();
  const price_discount = parseFloat((price * (1 - (prct_discount / 100)))).toFixed(2);

  /*
    ${
      isDiscounted
        ? `<strong class="price__current font-extra-bold text-red-600"><span class="money">${price} ${currency}</span></strong>
          <span class="price__compare text-gray-400 line-through ml-2"><span class="money">${compareAt} ${currency}</span></span>
          <span class="discount-badge ml-2 text-sm text-green-600">-${discountPercent}%</span>`
        : `<strong class="price__current font-extra-bold"><span class="money">${price} ${currency}</span></strong>`
    }
  */

  let cta = "Ajouter au panier";
  if (language == 'en') cta = "Add to cart";
  else if (language == 'de') cta = "In den Warenkorb legen";
  else if (language == 'it') cta = "Aggiungi al carrello";
  else if (language == 'es') cta = "Añadir al carrito";

  const html = `
    <div class="crosssell-item" data-handle="${cross.handle}">
      <img src="${cross.featuredImage?.url || ''}" alt="${cross.title}" loading="lazy" width="88">
      <div class="crosssell-info">
        <p class="regular-text mt-2 mb-1 font-light crosssell-title">${cross.title}</p>
        <div class="price">
          <div class="price__default">
            <strong class="price__current"><span class="money line-through">${price}</span> <span class="money font-extra-bold">${price_discount} ${currency}</span> <span class="font-red">-${discount}%</span></strong>
          </div>
        </div>
        <div class="flex">
          <quantity-input class="inline-block crosssell-qty">
            <div class="qty-input qty-input--combined inline-flex items-center w-full">
              <button type="button" class="qty-input__btn btn btn--minus" name="minus" aria-label="-"></button>
              <input type="number" class="qty-input__input input" id="custom-cross-sell-quantity-${variantId}" min="1" value="1">
              <button type="button" class="qty-input__btn btn btn--plus" name="plus" aria-label="+"></button>
            </div>
          </quantity-input>
          <button class="btn btn--primary btn--sm add-crosssell" data-id="${variantId}">${cta}</button>
        </div>
      </div>
    </div>
  `;

  const oldElement = container.querySelector(`.crosssell-item[data-handle="${cross.handle}"]`);
  if (oldElement) oldElement.remove();

  container.insertAdjacentHTML('beforeend', html);

  const btn = container.querySelector(`.add-crosssell[data-id="${variantId}"]`);
  const qtyInput = container.querySelector(`#custom-cross-sell-quantity-${variantId}`);

  btn.addEventListener('click', async () => {
    try {
      const quantity = parseInt(qtyInput.value, 10) || 1;

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      });

      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    } catch (err) {
      console.error('Erreur lors de l’ajout du cross-sell :', err);
    }
  });
}

/* variant list - price update */
document.addEventListener('on:variant:change', (e) => {
  const currentVariant = e.detail.variant;
  if (!currentVariant) return;

  const currentVariantOpt1 = currentVariant.option1;
  const currentVariantOpt2 = currentVariant.option2;
  const currentVariantOpt3 = currentVariant.option3;

  document.querySelectorAll('.opt-label.show-as-list').forEach((label) => {
    const optlist = label.dataset.optlist;
    
    let spans = label.querySelectorAll('span.all-price').forEach((s) => {
      s.style.display = 'none';
    });

    let span;
    if (optlist == 2) span = label.querySelector('span.all-price[data-opt1="' + currentVariantOpt1 + '"][data-opt2="' + currentVariantOpt2 + '"]');
    else if (optlist == 1) span = label.querySelector('span.all-price[data-opt1="' + currentVariantOpt1 + '"]');
    else if (optlist == 0) span = label.querySelector('span.all-price');

    if (span) span.style.display = 'inline';
  });
});